import React, { useState } from "react";
import { useSelector } from "react-redux";
import { getAssetSignature, imageBlobResizer } from "@iventis/utilities";
import { VenueBookmarkForm, VenueBookmarksView } from "@iventis/digital-twin";
import { VenueBookmark } from "@iventis/domain-model/model/venueBookmark";
import { AssetsPostRequest } from "@iventis/api-helpers";
import { AssetType } from "@iventis/domain-model/model/assetType";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { machineEventTypes, MappingEngine } from "@iventis/map-engine";
import { toast } from "@iventis/toasts/src/toast";
import { Content } from "@iventis/translations";
import { incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { dispatch, RootState } from "./state/root.store";
import { assetService, createAsset } from "./services/asset-service";
import { getVenueBookmarks, deleteVenueBookmark, createVenueBookmark } from "./services/venue-bookmarks";
import { eventStream } from "./event-stream";
import { map } from "./state/map.slice";

const BOOKMARKS_QUERY_KEY = "BOOKMARKS";

export const PlanVenueBookmarksComponent = ({ getSnapshotDataUrl }: { getSnapshotDataUrl: () => Promise<string> }) => {
    const { digitalTwinEngineData, position, showBookmarks, user, mappingEngine } = useSelector((state: RootState) => ({
        digitalTwinEngineData: state.mapReducer.mapModule.digitalTwinEngineData,
        position: state.mapReducer.mapModule.position,
        showBookmarks: state.mapReducer.showBookmarks,
        user: state.auth.user,
        mappingEngine: state.mapReducer.mappingEngine,
    }));

    const [creatingBookmark, setCreatingBookmark] = useState<boolean>(false);

    const queryClient = useQueryClient();
    const { data: venueBookmarks, isLoading } = useQuery([BOOKMARKS_QUERY_KEY, user.projectId], () => getVenueBookmarks());

    const handleBookmarkCreate = async (nameValue: string) => {
        try {
            const dataUrlRaw = await getSnapshotDataUrl();

            // Convert to blob and resize
            const blob = await fetch(dataUrlRaw).then((res) => res.blob());
            const dataUrl = await imageBlobResizer(blob, 512);

            // Create thumbnail asset
            const req: AssetsPostRequest = { dataUrl, type: AssetType.VenueBookmarkImage, name: nameValue, tags: [] };
            const asset = await createAsset(req);

            // Create bookmark
            const bookmarkPartial: Partial<VenueBookmark> = {
                name: nameValue,
                cameraMode: digitalTwinEngineData.cameraMode.value,
                bearing: position.value.bearing,
                latitude: position.value.lat,
                longitude: position.value.lng,
                pitch: position.value.pitch,
                zoom: position.value.zoom,
                wgs84Altitude: position.value.altitude,
                thumbnailAssetId: asset.id,
                venueId: digitalTwinEngineData.venueId,
            };

            await createVenueBookmark(bookmarkPartial);
            incrementMonitoringCount({ name: MetricName.VENUE_BOOKMARK_CREATED });

            queryClient.invalidateQueries([BOOKMARKS_QUERY_KEY, user.projectId]);
        } catch (error) {
            toast.error({
                message: Content.map10.venue_bookmark_creation_error.message,
                title: Content.map10.venue_bookmark_creation_error.title,
            });
        }
        setCreatingBookmark(false);
    };

    const handleBookmarkCancel = () => {
        setCreatingBookmark(false);
    };

    const handleVenueBookmarkSelect = (bookmark: VenueBookmark) => {
        eventStream.next({
            type: machineEventTypes.setPosition,
            payload: {
                bearing: bookmark.bearing,
                lat: bookmark.latitude,
                lng: bookmark.longitude,
                pitch: bookmark.pitch,
                zoom: bookmark.zoom,
                cameraMode: bookmark.cameraMode,
                altitude: bookmark.wgs84Altitude,
            },
        });

        incrementMonitoringCount({ name: MetricName.VENUE_BOOKMARK_SELECTED });

        dispatch(map.actions.setCameraMode({ value: bookmark.cameraMode, stamp: false }));
    };

    const handleVenueBookmarkDelete = async (id: string) => {
        await deleteVenueBookmark(id);
        queryClient.invalidateQueries([BOOKMARKS_QUERY_KEY, user.projectId]);
    };

    const handleVenueBookmarkAdd = () => {
        // If no venueId in the state, show a toast saying that they must be near a venues
        if (digitalTwinEngineData.venueId == null || digitalTwinEngineData.venueId?.length <= 0) {
            toast.error({
                message: Content.map10.venue_bookmark_no_venue_error.message,
                title: Content.map10.venue_bookmark_no_venue_error.title,
            });
        } else {
            setCreatingBookmark(true);
        }
    };

    const getAssetUrl = async (assetId: string) => {
        const asset = await assetService.getAsset(assetId);
        return getAssetSignature(asset.assetUrl, asset.authoritySignature);
    };

    return (
        <>
            {creatingBookmark && <VenueBookmarkForm onBookmarkCreate={handleBookmarkCreate} onCancel={handleBookmarkCancel} />}
            {showBookmarks && mappingEngine === MappingEngine.Unreal && (
                <VenueBookmarksView
                    venueBookmarks={venueBookmarks}
                    venueBookmarkOperations={{
                        onVenueBookmarkSelect: handleVenueBookmarkSelect,
                        onVenueBookmarkDelete: handleVenueBookmarkDelete,
                        onVenueBookmarkCreate: handleVenueBookmarkAdd,
                    }}
                    className="venue-bookmarks"
                    getAssetUrl={getAssetUrl}
                    loading={isLoading}
                    onClose={() => {
                        dispatch(map.actions.setShowBookmarks(false));
                    }}
                />
            )}
        </>
    );
};
